import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { FluentProvider, createDarkTheme, createLightTheme } from "@fluentui/react-components";
import type { BrandVariants, Theme } from "@fluentui/react-components";
import { useCatalogStore } from "@/store/catalog-store";

/** PRD §8.6 accent blue (#0A7AFF) as Fluent brand ramp. */
const brand: BrandVariants = {
  10: "#020305",
  20: "#071018",
  30: "#0B1F33",
  40: "#0C2F4F",
  50: "#0A3F6C",
  60: "#06508A",
  70: "#0061A9",
  80: "#0073C8",
  90: "#0A7AFF",
  100: "#3A8FFF",
  110: "#5BA3FF",
  120: "#78B6FF",
  130: "#94C8FF",
  140: "#AFD9FF",
  150: "#CBE9FF",
  160: "#E8F4FF",
};

/**
 * 사이드 항목은 14px, 본문 흐름은 15px. 표 셀은 13px을 유지한다.
 */
const type: Partial<Theme> = {
  fontFamilyBase: "var(--font-sans)",
  fontFamilyNumeric: "var(--font-sans)",
  fontSizeBase300: "13px",
  lineHeightBase300: "18px",
};

/**
 * 사이드바는 따뜻한 회색, 본문은 흰 면.
 * 글자는 본문 #3f3f46, 메타 #636363. 값은 styles/globals.css와 같다.
 */
const lightInk: Partial<Theme> = {
  colorNeutralForeground1: "#3f3f46",
  colorNeutralForeground2: "#3f3f46",
  colorNeutralForeground3: "#636363",
  colorNeutralForeground4: "#636363",
  colorNeutralStroke2: "rgba(15, 13, 10, 0.08)",
  colorNeutralStroke1: "rgba(15, 13, 10, 0.12)",
  colorNeutralBackground3Hover: "var(--surface-sidebar-hover)",
  colorNeutralBackground3Pressed: "var(--surface-sidebar-pressed)",
  colorNeutralBackground3Selected: "#ffffff",
  colorNeutralBackground1Hover: "#f3f1ef",
  colorNeutralBackground1Pressed: "#eceae7",
  colorNeutralBackground1Selected: "#f3f1ef",
};

/**
 * 사이드와 본문 바닥은 제품 기본 면이다.
 * 값은 styles/globals.css의 --surface-sidebar, --surface-canvas.
 * 카드·헤더·입력은 colorNeutralBackground1(흰색)을 유지한다.
 */
const surfaces: Partial<Theme> = {
  colorNeutralBackground3: "var(--surface-sidebar)",
  colorNeutralBackground2: "var(--surface-canvas)",
};

/**
 * 다크 글자·선·호버. 값은 styles/globals.css .dark 와 같다.
 * 본문 #f5f5f7, 메타 #98989d. 카드(Background1)는 Fluent 어두운 면을 유지한다.
 */
const darkInk: Partial<Theme> = {
  colorNeutralForeground1: "#f5f5f7",
  colorNeutralForeground2: "#f5f5f7",
  colorNeutralForeground3: "#98989d",
  colorNeutralForeground4: "#98989d",
  colorNeutralStroke2: "rgba(255, 255, 255, 0.1)",
  colorNeutralStroke1: "rgba(255, 255, 255, 0.14)",
  colorNeutralBackground3Hover: "#3a3a3c",
  colorNeutralBackground3Pressed: "#48484a",
  colorNeutralBackground3Selected: "#3a3a3c",
  colorNeutralBackground1Hover: "#3a3a3c",
  colorNeutralBackground1Pressed: "#48484a",
  colorNeutralBackground1Selected: "#3a3a3c",
};

const lightTheme: Theme = {
  ...createLightTheme(brand),
  ...type,
  ...surfaces,
  ...lightInk,
  colorBrandForeground1: "#0A7AFF",
  colorBrandForeground2: "#0061A9",
};

const darkTheme: Theme = {
  ...createDarkTheme(brand),
  ...type,
  ...surfaces,
  ...darkInk,
  colorBrandForeground1: "#5BA3FF",
  colorBrandForeground2: "#94C8FF",
};

export function FluentThemeRoot({ children }: { children: ReactNode }) {
  const pref = useCatalogStore((s) => s.theme);
  const [mode, setMode] = useState<"light" | "dark">(() => resolveMode(pref));
  const [body, setBody] = useState<HTMLElement | null>(null);
  const theme = mode === "dark" ? darkTheme : lightTheme;

  useEffect(() => {
    setBody(document.body);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const next = resolveMode(pref, mq.matches);
      setMode(next);
      document.documentElement.classList.toggle("dark", next === "dark");
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [pref]);

  return (
    <FluentProvider
      id="fluent-root"
      theme={theme}
      style={{ height: "100%", minHeight: "100%", backgroundColor: "transparent" }}
    >
      {children}
      {body &&
        createPortal(
          <FluentProvider
            id="dialog-layer"
            theme={theme}
            style={{ position: "absolute", zIndex: 1000000 }}
          />,
          body,
        )}
    </FluentProvider>
  );
}

function resolveMode(pref: "system" | "light" | "dark", systemDark = typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches): "light" | "dark" {
  if (pref === "dark") return "dark";
  if (pref === "light") return "light";
  return systemDark ? "dark" : "light";
}
